import React, { memo, useEffect, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  message,
  Row,
  Col,
  Typography,
  Divider,
  Input,
  Button,
  Popconfirm,
} from "antd";
import { usePutData } from "app/hooks/hooks";
import FormModal from "app/components/FormModal/FormModal";
import { IModalType } from "app/types/modalType";
import { UsersApiV2UsersMeViewsPutRequest, UserViewsDTO } from "@generated/v2";
import ErrorFallbackUI from "app/components/ErrorFallbackUI";
import PageFilter from "app/components/PageFilteView/PageFilter";
import Columns from "./Columns";
import styles from "./SettingModal.module.scss";
import { FormItem } from "app/components/Form/Form";
import { useTestRecords } from "app/state/testRecords";
import { UserViewContext } from "app/store/contexts/userContextView";
import { usersMeViewsPut } from "app/api/v2/usersApi";
import { cloneDeep } from "lodash";
import { useLocation, useHistory } from "react-router-dom";
import qs from "query-string";
import { formatRoute } from "react-router-named-routes";

const { Text } = Typography;

const SettingModal = ({ show, close, route }: IModalType) => {
  const { t } = useTranslation();
  const [isDelete, setIsDelete] = useState(false);
  const { columns, views, handleViews } = useTestRecords();
  const { userInfoView } = useContext(UserViewContext);
  const location = useLocation();
  const history = useHistory();
  const cloneViews = cloneDeep([...views?.views]);

  const currentSearch = qs.parse(location.search);
  const viewId = Number(currentSearch?.viewId);
  const lastUpdated = views?.lastUpdated;

  const getName = () => {
    const view = cloneViews?.find(view => view.id === viewId);
    return view?.name;
  };

  const {
    loading,
    isError,
    isSuccess,
    data: saveData,
    setBody,
  } = usePutData<UserViewsDTO, UsersApiV2UsersMeViewsPutRequest>(
    usersMeViewsPut
  );

  /**
   * Listen for the state of the save data hook, and display status
   * message, and closes on success.
   */
  useEffect(() => {
    if (isSuccess) {
      if (isDelete) {
        message.success(t("testRecords.userViewsSuccessUpdate"));
        setIsDelete(false);
        setTimeout(() => {
          close();
          const newRoute = formatRoute(route);

          history.replace({
            pathname: newRoute,
            search: qs.stringify(
              { viewId: undefined },
              {
                arrayFormat: "comma",
              }
            ),
          });
        }, 1200);
      } else {
        close();
        message.success(
          !viewId
            ? t("testRecords.userViewsSuccess")
            : t("testRecords.userViewsSuccessUpdate")
        );
      }
    } else if (isError) {
      message.error(t("testRecords.userViewsError"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isError, route]);

  /**
   * Handles submit
   * @param formData Inputted data from form
   */
  const handleSubmit = (formData: any) => {
    let requestPayload = {};
    const filters: any = cloneDeep(userInfoView.filters);
    delete filters.customer;
    delete filters.customerId;
    delete filters.site;
    delete filters.siteId;

    filters.dates = [];

    const newCreateView = {
      id: viewId ? viewId : Math.floor(Math.random() * (100000 - 1 + 1) + 1),
      name: formData.name,
      filters,
      columns,
    };

    if (viewId) {
      const editViews = cloneViews?.findIndex(view => view.id === viewId);
      cloneViews[editViews] = newCreateView;

      requestPayload = {
        views: cloneViews,
        lastUpdated,
      };
    } else {
      cloneViews.unshift(newCreateView);
      requestPayload = {
        views: cloneViews,
        lastUpdated,
      };
    }

    formData = {
      updateUserViewsCommand: requestPayload,
    };
    setBody(formData);
  };

  const handleDelete = () => {
    const newViews = cloneViews?.filter(view => view.id !== viewId);
    const formData = {
      updateUserViewsCommand: {
        views: newViews,
        lastUpdated: views?.lastUpdated,
      },
    };
    setBody(formData);
    setIsDelete(true);
  };

  useEffect(() => {
    if (!!saveData) {
      handleViews(saveData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveData]);

  return (
    <ErrorFallbackUI>
      <FormModal
        header={t("testRecords.viewSetting")}
        visible={show}
        onClose={close}
        onSubmit={handleSubmit}
        localization={{
          unsavedMessage: t("default.warningUnsavedChanges"),
        }}
        loadingSubmit={loading}
      >
        <Row gutter={[48, 16]}>
          <Col xs={{ span: 24 }} lg={{ span: 12 }}>
            <Text strong>NAME</Text>
            <br />
            <br />
            <FormItem
              name="name"
              options={{
                initialValue: viewId ? getName() : undefined,
                rules: [
                  {
                    required: true,
                    whitespace: true,
                    message: t("testRecords.ViewNameErrorRequired"),
                  },
                ],
              }}
            >
              <Input placeholder={t("testRecords.viewName")} size="large" />
            </FormItem>
            <Divider className={styles.divider} />
            <Text className={styles.filters} strong>
              FILTERS
            </Text>
            <br />
            <br />
            <PageFilter
              deviceFilter
              siteFilter
              dateFilter
              routesFilter
              testResultFilter
              usersFilter
              measurementMethodFilter
              xl={24}
              lg={24}
            />
          </Col>
          <Col xs={{ span: 24 }} lg={{ span: 12 }}>
            <Text className={styles.header} strong>
              COLUMNS SETTING
            </Text>
            <Columns viewId={viewId} />
          </Col>
        </Row>

        {!!viewId && (
          <Popconfirm
            title={t("testRecords.viewDeleteMessage")}
            onConfirm={handleDelete}
            okText="Yes"
            cancelText="No"
            className={styles.deleteBtn}
          >
            <Button size="large" type="danger" ghost>
              Delete View
            </Button>
          </Popconfirm>
        )}
      </FormModal>
    </ErrorFallbackUI>
  );
};

export default memo(SettingModal);
